import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Avatar,
  Alert,
  InputAdornment,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";

/**
 * Modal para configurar el stock mínimo de una ubicación específica
 */
const MinStockModal = ({ open, onClose, product, location, onSubmit }) => {
  const [minStock, setMinStock] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && location) {
      setMinStock(location.minStock || 0);
      setError("");
    }
  }, [open, location]);

  if (!product || !location) return null;

  const handleSubmit = () => {
    if (minStock < 0) {
      setError("El stock mínimo debe ser mayor o igual a 0");
      return;
    }

    // Para técnicos y almacenes, usar locationId
    // locationId es el _id de InventoryLocation
    const locId = location.locationId;
    
    if (!locId) {
      setError("No se pudo identificar la ubicación");
      return;
    }

    onSubmit({
      itemId: product._id,
      locationId: locId,
      minStock: parseFloat(minStock) || 0,
    });
  };

  const isWarehouse = location.locationName !== undefined;
  const displayName = location.locationName || location.technicianName || "Ubicación";
  const currentStock = location.stock || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "warning.main",
          color: "white",
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 48,
              height: 48,
            }}
          >
            <WarningAmberIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Configurar Stock Mínimo
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {product.name}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Información de la ubicación */}
          <Box
            sx={{
              bgcolor: "grey.50",
              borderRadius: 2,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" mb={1}>
              {isWarehouse ? (
                <WarehouseIcon color="primary" />
              ) : (
                <EngineeringIcon color="info" />
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Ubicación
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {displayName}
                </Typography>
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Stock actual: <strong>{currentStock} {product.unit || "u"}</strong>
            </Typography>
          </Box>

          {/* Campo de stock mínimo */}
          <TextField
            label="Stock Mínimo"
            type="number"
            value={minStock}
            onChange={(e) => {
              setMinStock(e.target.value);
              setError("");
            }}
            fullWidth
            helperText="Recibirás alertas cuando el stock esté por debajo de este valor"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    {product.unit || "u"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              step: product.unit === "ml" || product.unit === "lt" ? 0.1 : 1,
            }}
          />

          {/* Preview del estado */}
          {minStock > 0 && (
            <Alert
              severity={currentStock < minStock ? "warning" : "success"}
              sx={{ borderRadius: 2 }}
            >
              {currentStock < minStock
                ? `⚠️ El stock actual está por debajo del mínimo (${minStock})`
                : `✅ El stock actual está por encima del mínimo (${minStock})`}
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          size="large"
          startIcon={<WarningAmberIcon />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MinStockModal;
